import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TextField,
  Typography,
  MenuItem,
  Grid,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { FormCheck, DropdownButton, Dropdown, ButtonGroup } from 'react-bootstrap';

import { floatVal, intVal, Nullable } from '@jamesgmarks/utilities';

import { getMany } from '../../../redux/features/dynamic-hydra/actions';
import { StyledTableCell, StyledTableRow } from '../../parts/mui/StyledTables';
import { decodeDisplayName, makeYmd, toDollarAmount } from '../../../app-utils';
import {
  getCreditNoteAppliedInvoices,
  getCreditNoteAvailableCredit,
  getCreditNoteSourceInvoices,
  getCreditNoteTotal,
  ICreditNoteInvoiceLink,
  isCreditNoteFullyApplied,
} from '../../../app-utils/credit-notes';
import { useAppSelector } from '../../../redux/hooks';
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  IQueryCommandOptions, INestedWhereCommand, In, Between, IWhereCommand, TWhereOperator,
} from '@llws/dynamic-router-utils';
import { showMessage } from '../../../redux/features/messaging/actions';
import { ClientMultiSelectDropdown } from 'src/components/parts/ClientMultiSelectDropdown';
import { OwnershipGroupDropdown } from '../../parts/OwnershipGroupDropdown';
import { IClientDropdownItem } from 'src/entity-interfaces/IClientDropdownItem';
import {
  EHydraCreditNoteState,
  getHydraCreditNoteStateDisplayName,
} from 'src/entity-interfaces/EHydraCreditNoteState';
import { SendDocumentsModal } from '../Invoices/SendDocumentsModal';
import { EDocumentType } from '@hydra/internal';
import {
  publishCreditNotes,
  triggerCreditNotePdfGeneration,
  deleteCreditNotes,
} from 'src/redux/features/credit-notes/actions';
import pdfFileIcon from '../../../assets/images/pdf.png';
import { REACT_APP_API_ROOT_URI } from '../../../App';
import { typedApiFetch } from '../../../redux/utils';
import { TCreditNoteWithBillingContacts } from 'src/types/TCreditNoteWithBillingContacts';
import { BulkSubscriptionCreditModal } from './BulkSubscriptionCredit/BulkSubscriptionCreditModal';
import { MassCreditUploadModal } from './MassCreditUpload';
import { ApplyToInvoiceModal } from './ApplyToInvoiceModal';

interface CreditNoteListDataRowProps {
  creditNote: TCreditNoteWithBillingContacts;
  checked: boolean;
  setSelected: (creditNoteId: number, selected: boolean) => void;
  onDelete: (creditNoteId: number) => void;
}

/** Sentinel value used as a virtual filter option in the state dropdown to represent fully-applied credit notes. */
const APPLIED_STATE_FILTER = 'applied';

/** Menu label for a state filter option, including the virtual {@link APPLIED_STATE_FILTER} one. */
const getStateFilterLabel = (state: string) => (
  state === APPLIED_STATE_FILTER ? 'Applied' : getHydraCreditNoteStateDisplayName(state)
);

/** Number of invoice chips rendered inline before the remainder collapses into a `+N more` chip. */
const MAX_VISIBLE_INVOICE_CHIPS = 3;

/** Which of a note's two invoice relationships a chip list is showing. */
type TInvoiceLinkTone = 'source' | 'applied';

/**
 * Explicit chip colours per relationship.
 *
 * MUI's `primary`/`secondary` outlined chips resolve to mid-tone text and a low-alpha border
 * that wash out against the dark row, which left the invoice numbers — the most useful thing in
 * these two columns — the least readable. These are picked to clear a comfortable contrast on
 * that background while keeping the two relationships tellable apart by hue as well as by
 * column: amber draws FROM, blue applies TO.
 */
const INVOICE_LINK_TONES: Record<TInvoiceLinkTone, { text: string, border: string, fill: string }> = {
  source: { text: '#FFCC80', border: 'rgba(255, 204, 128, 0.65)', fill: 'rgba(255, 204, 128, 0.12)' },
  applied: { text: '#90CAF9', border: 'rgba(144, 202, 249, 0.65)', fill: 'rgba(144, 202, 249, 0.12)' },
};

interface IInvoiceLinkChipsProps {
  links: ICreditNoteInvoiceLink[];
  emptyLabel: string;
  tone: TInvoiceLinkTone;
}

/**
 * Renders a credit note's related invoices as a compact list of chips, each linking to that
 * invoice and captioned with the amount tying it to the note.
 *
 * The list is capped at {@link MAX_VISIBLE_INVOICE_CHIPS} so a note applied across many invoices
 * cannot blow out the row height; the overflow count stays visible and the full set is listed in
 * the tooltip.
 */
const InvoiceLinkChips = ({ links, emptyLabel, tone }: IInvoiceLinkChipsProps) => {
  if (links.length === 0) {
    // `text.secondary` renders near-invisible on the dark row background, and "Not applied" is
    // meaningful state rather than decoration, so it gets a legible dimmed white instead.
    return (
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.72)', whiteSpace: 'nowrap' }}>
        {emptyLabel}
      </Typography>
    );
  }

  const visibleLinks = links.slice(0, MAX_VISIBLE_INVOICE_CHIPS);
  const hiddenCount = links.length - visibleLinks.length;
  const { text, border, fill } = INVOICE_LINK_TONES[tone];

  return (
    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
      {visibleLinks.map((link) => (
        <Link key={link.invoiceId} to={`/invoices/${link.invoiceId}`} style={{ textDecoration: 'none' }}>
          <Tooltip title={`${toDollarAmount(link.amount)} — invoice ${link.invoiceId}`}>
            <Chip
              size="small"
              variant="outlined"
              clickable
              label={link.invoiceNumber ?? `#${link.invoiceId}`}
              sx={{
                backgroundColor: fill,
                borderColor: border,
                color: text,
                fontWeight: 500,
                '&:hover': { backgroundColor: fill, borderColor: text },
                // The chip's own label colour is set on the inner span, so the Chip-level
                // `color` alone does not reach it.
                '& .MuiChip-label': { color: text },
              }}
            />
          </Tooltip>
        </Link>
      ))}
      {hiddenCount > 0 && (
        <Tooltip
          // Only the HIDDEN links: the visible ones are already on screen as their own chips, so
          // listing them again is just noise to a reader checking what the overflow is hiding.
          title={links
            .slice(MAX_VISIBLE_INVOICE_CHIPS)
            .map((link) => `${link.invoiceNumber ?? `#${link.invoiceId}`}: ${toDollarAmount(link.amount)}`)
            .join(', ')}
        >
          <Chip
            size="small"
            variant="filled"
            label={`+${hiddenCount} more`}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
              '& .MuiChip-label': { color: 'rgba(255, 255, 255, 0.87)' },
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

const CreditNoteListDataRow = ({
  creditNote,
  checked,
  setSelected,
  onDelete,
}: CreditNoteListDataRowProps) => {
  // Check if PDF is available based on pdfGenerations array
  const hasDownload = (creditNote.pdfGenerations ?? []).length > 0;
  const totalCredit = getCreditNoteTotal(creditNote);
  const availableCredit = getCreditNoteAvailableCredit(creditNote);
  const stateDisplay = isCreditNoteFullyApplied(creditNote)
    ? 'Applied'
    : getHydraCreditNoteStateDisplayName(creditNote.state);

  // `ownershipGroup` is hydrated from the LIFT database by `creditNotesMiddleware`; it is absent
  // (not merely null) when no note in the response matched an ownership group, hence the `??` chain
  // down to the raw id and then to a placeholder.
  //
  // The name is decoded for display: `ownership_groups.name` stores entity-encoded text, so a
  // group genuinely called "61 & 65 Roy Street" is held as `61 &amp; 65 Roy Street`. Ownership
  // groups are frequently named after the single property they cover, which is why this line
  // reads like a building name — it IS the group's name, not the building's, so it is left as
  // the subordinate line under the client.
  const ownershipGroupName = decodeDisplayName(creditNote.ownershipGroup?.name);
  const ownershipGroupLabel = (
    ownershipGroupName ?? (creditNote.ownershipGroupId ? `Group ${creditNote.ownershipGroupId}` : null)
  );
  const clientName = decodeDisplayName(creditNote.billingAccount?.clients?.[0]?.clientName);
  const sourceInvoices = getCreditNoteSourceInvoices(creditNote);
  const appliedInvoices = getCreditNoteAppliedInvoices(creditNote);

  return (
    <StyledTableRow dark={true} hover={true}>
      <StyledTableCell
        dark={true}
        className="invoices-checkbox-column"
        style={{ textAlign: 'center' }}
        onClick={() => setSelected(creditNote.id, !checked)}
      >
        <FormCheck
          checked={checked}
          onChange={(e) => setSelected(creditNote.id, e.target.checked)}
        />
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        onClick={() => setSelected(creditNote.id, !checked)}>{
          creditNote.id}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {creditNote.noteNumber}
        &nbsp;
        <img
          src={pdfFileIcon}
          alt="pdf"
          height="20px"
          style={{
            filter: 'grayscale(' + (hasDownload ? '0' : '1') + ')',
          }}
          title={hasDownload ? 'Download available' : 'Download not available'}
        />
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {stateDisplay}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {creditNote.billingAccountId}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        onClick={() => setSelected(creditNote.id, !checked)}>
        <Typography variant="body2" fontWeight={600} lineHeight={1.35}>
          {clientName ?? 'N/A'}
        </Typography>
        <Tooltip title={ownershipGroupLabel ?? 'No ownership group'}>
          <Typography
            variant="caption"
            component="span"
            lineHeight={1.35}
            sx={{
              // `text.secondary` is too dim to read against the dark row background; this keeps
              // the group subordinate to the client without dropping below a legible contrast.
              color: 'rgba(255, 255, 255, 0.72)',
              display: 'block',
              maxWidth: '26ch',
              overflow: 'hidden',
              textAlign: 'center',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ownershipGroupLabel ?? 'No ownership group'}
          </Typography>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell dark={true}>
        <InvoiceLinkChips links={sourceInvoices} emptyLabel="No source invoices" tone="source" />
      </StyledTableCell>
      <StyledTableCell dark={true}>
        <InvoiceLinkChips links={appliedInvoices} emptyLabel="Not applied" tone="applied" />
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        align="right"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {toDollarAmount(floatVal(creditNote.subtotalAmount ?? 0))}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        align="right"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {toDollarAmount(totalCredit)}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        align="right"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {toDollarAmount(availableCredit)}
      </StyledTableCell>
      <StyledTableCell
        dark={true}
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => setSelected(creditNote.id, !checked)}>
        {creditNote.noteDate ? makeYmd(new Date(creditNote.noteDate), true) : 'N/A'}
      </StyledTableCell>
      <StyledTableCell dark={true}>
        <Dropdown as={ButtonGroup} size="sm">
          <Link to={`/credit_notes/${creditNote.id}`}>
            <Button size='small' color='primary' variant='contained'>
              View
            </Button>
          </Link>

          <Dropdown.Toggle split variant="secondary" id="dropdown-split-basic" />

          <Dropdown.Menu>
            <Dropdown.Item onClick={(e) => setSelected(creditNote.id, !checked)}>Select</Dropdown.Item>

            {hasDownload && (
              <Dropdown.Item
                as={Button}
                onClick={async (e) => {
                  const url = `${REACT_APP_API_ROOT_URI}/downloads/download_credit_note_pdf/${creditNote.noteNumber}`;
                  const response = await typedApiFetch(url);
                  const file = window.URL.createObjectURL(await response.blob());
                  const a = Object.assign(document.createElement('a'), {
                    href: file,
                    download: `${creditNote.noteNumber}.pdf`,
                  });
                  document.body.appendChild(a);
                  a.click(); a.remove();
                }}
                disabled={!hasDownload}
              >
                Download PDF
              </Dropdown.Item>
            )}

            <Dropdown.Item
              onClick={() => onDelete(creditNote.id)}
              disabled={creditNote.state === EHydraCreditNoteState.deleted}
              style={{ color: creditNote.state === EHydraCreditNoteState.deleted ? 'gray' : 'red' }}
              title={creditNote.state === EHydraCreditNoteState.deleted ? "Credit note is already deleted" : ""}
            >
              Delete Credit Note
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export const CreditNoteListView = () => {
  const creditNotes = useAppSelector(state => state.dynamicHydra.data.credit_notes?.list ?? []);
  const loadedState = useAppSelector(state => state.dynamicHydra.data.credit_notes?.loadedState);
  const totalRecords = useAppSelector(state => state.dynamicHydra.metaData?.totalRecords ?? 0);

  const [page, setPage] = useState(0); // Current page (0-indexed)
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [noteNumberSearch, setNoteNumberSearch] = useState(''); // Search term for credit note number
  const [filters, setFilters] = useState<{
    states: string[];
    startDate: Nullable<Date>;
    endDate: Nullable<Date>;
    createdStartDate: Nullable<Date>;
    createdEndDate: Nullable<Date>;
    minSubtotal: string;
    maxSubtotal: string;
  }>({
    states: [],
    startDate: null,
    endDate: null,
    createdStartDate: null,
    createdEndDate: null,
    minSubtotal: '',
    maxSubtotal: '',
  }); // Ensure startDate and endDate are typed as Nullable<Date>
  const [searchClients, setSearchClients] = useState<Nullable<IClientDropdownItem[]>>(null);
  const [searchOwnershipGroupId, setSearchOwnershipGroupId] = useState<Nullable<number>>(null);
  const [sortField, setSortField] = useState<string>('id'); // Default sort field
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // Default sort direction

  const [selectedCreditNotes, setSelectedCreditNotes] = useState<number[]>([]);
  const [allChecked, setAllChecked] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBulkSubscriptionModal, setShowBulkSubscriptionModal] = useState(false);
  const [showMassCreditUploadModal, setShowMassCreditUploadModal] = useState(false);
  const [showApplyToInvoiceModal, setShowApplyToInvoiceModal] = useState(false);
  const [isGeneratingPdfs, setIsGeneratingPdfs] = useState(false);

  const stateOptions = [...Object.values(EHydraCreditNoteState), APPLIED_STATE_FILTER];

  // The real `credit_notes.state` values among the selections. "Applied" is NOT one of them: it is
  // a virtual option (see APPLIED_STATE_FILTER) derived from how much credit is left on a note, so
  // it is carried separately everywhere the two are used.
  const selectedRealStates = useMemo(
    () => filters.states.filter((state) => state !== APPLIED_STATE_FILTER),
    [filters.states],
  );
  const isAppliedSelected = filters.states.includes(APPLIED_STATE_FILTER);
  const isPublishedSelected = selectedRealStates.includes(EHydraCreditNoteState.sent);

  // Selecting several states filters to their UNION, so a note is kept when it matches ANY of them.
  //
  // "Published" and "Applied" are EXCLUSIVE buckets, matching the State column: a fully-applied
  // note (stored state `sent`, displayed as "Applied") matches only the "Applied" option, never
  // "Published". Selecting both shows every `sent` note.
  //
  // The server cannot express "fully consumed", so whenever the split matters — either bucket is
  // selected — it returns every `sent` note and the split happens here. Every other state is
  // filtered exactly server-side. NOTE: this client-side pass means a page can render fewer rows
  // than `rowsPerPage`, and the pagination's `totalRecords` counts all `sent` notes; fixing that
  // requires computing "fully applied" server-side.
  const filteredCreditNotes = useMemo(() => {
    if (!isAppliedSelected && !isPublishedSelected) { return creditNotes; }

    return creditNotes.filter((creditNote) => (
      isCreditNoteFullyApplied(creditNote)
        ? isAppliedSelected
        : selectedRealStates.includes(creditNote.state)
    ));
  }, [creditNotes, isAppliedSelected, isPublishedSelected, selectedRealStates]);

  // Track PDF generation queue length
  const creditNoteDownloadQueueLength = useAppSelector(
    (state) => state.documents.downloadQueues?.[EDocumentType['credit-note']]?.queueLength ?? 0,
  );

  // Helper function to create properly typed IWhereCommand objects
  const createWhereCommand = (field: string, value: string, operator?: TWhereOperator): IWhereCommand => ({
    field,
    value,
    operator,
  });

  const fetchData = () => {
    const queryStates = [
      ...new Set(isAppliedSelected ? [ ...selectedRealStates, EHydraCreditNoteState.sent ] : selectedRealStates),
    ];

    const whereCommands: INestedWhereCommand[] = [
      // Note Number search filter
      ...(noteNumberSearch
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.noteNumber', `${noteNumberSearch}`, 'like'),
          ],
        }]
        : []
      ),

      // State filter. Selecting nothing means "All", so no command is added at all.
      //
      // "Applied" contributes `sent` to the query, because a fully-applied note IS a sent one —
      // the narrowing to "fully consumed" then happens in `filteredCreditNotes`. Deduped, so
      // selecting both Published and Applied asks for `sent` once.
      ...(queryStates.length > 0
        ? [{
          condition: 'and' as const,
          whereCommands: [
            In('cn.state', queryStates),
          ],
        }]
        : []
      ),

      // Client filter
      ...(searchClients && searchClients.length > 0
        ? [{
          condition: 'and' as const,
          whereCommands: [
            In('lc.clientId', searchClients.map(c => c.id)),
          ],
        }]
        : []
      ),

      // Date filters
      ...(filters.startDate && filters.endDate
        ? [{
          condition: 'and' as const,
          whereCommands: [
            Between('cn.noteDate', makeYmd(filters.startDate), makeYmd(filters.endDate)),
          ],
        }]
        : []
      ),

      ...(filters.startDate && !filters.endDate
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.noteDate', makeYmd(filters.startDate), 'gte'),
          ],
        }]
        : []
      ),

      ...((!filters.startDate && filters.endDate)
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.noteDate', makeYmd(filters.endDate), 'lte'),
          ],
        }]
        : []
      ),

      // Ownership group filter. `ownership_group_id` is a plain column on `credit_notes`, so this
      // filters server-side even though the group's NAME has to be hydrated from the LIFT database.
      ...(searchOwnershipGroupId
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.ownershipGroupId', `${searchOwnershipGroupId}`),
          ],
        }]
        : []
      ),

      // Created-date filters (when the note was raised in Hydra, as opposed to its note date).
      ...(filters.createdStartDate
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.created', makeYmd(filters.createdStartDate), 'gte'),
          ],
        }]
        : []
      ),

      ...(filters.createdEndDate
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.created', makeYmd(filters.createdEndDate), 'lte'),
          ],
        }]
        : []
      ),

      // Amount filters. NOTE: these bound `subtotalAmount` (tax-EXCLUSIVE), which is the only
      // amount stored on the row. The "Total Credit" and "Available Credit" columns are computed
      // client-side and are therefore NOT filterable here; the input labels say so explicitly.
      ...(filters.minSubtotal.trim()
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.subtotalAmount', `${floatVal(filters.minSubtotal)}`, 'gte'),
          ],
        }]
        : []
      ),

      ...(filters.maxSubtotal.trim()
        ? [{
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('cn.subtotalAmount', `${floatVal(filters.maxSubtotal)}`, 'lte'),
          ],
        }]
        : []
      ),
    ];

    const queryOptions: IQueryCommandOptions = {
      alias: 'cn',
      orderBy: [{ field: `cn.${sortField}`, direction: sortDirection }],
      page: page + 1,
      limit: rowsPerPage,
      relations: [
        { path: 'cn.billingAccount', alias: 'ba' },
        { path: 'ba.clients', alias: 'lc' },
        { path: 'cn.credits', alias: 'cr' },
        { path: 'cr.invoice', alias: 'invoice' },
        { path: 'cn.creditsApplied', alias: 'ca' },
        // The invoices the note has been applied TO, as distinct from the `cr.invoice` source
        // invoices above. Safe to add: the dynamic router paginates with TypeORM `take`/`skip` and
        // counts with `getCount()`, both of which work on DISTINCT parent ids, so widening the
        // join fan-out does not corrupt the page size or `totalRecords`.
        { path: 'ca.invoice', alias: 'appliedInvoice' },
      ],
      where: whereCommands,
    };

    getMany('credit_notes', queryOptions);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page, rowsPerPage, noteNumberSearch, filters, searchClients, searchOwnershipGroupId, sortField, sortDirection,
  ]);

  useEffect(() => {
    // Update allChecked state when selectedCreditNotes changes
    setAllChecked(selectedCreditNotes.length === filteredCreditNotes.length && filteredCreditNotes.length > 0);
  }, [selectedCreditNotes, filteredCreditNotes]);

  // Set an credit note row's checkbox to either checked or unchecked, explicitly.
  const setSelected = useCallback(
    (creditNoteId: number, newValue: boolean) => {
      setSelectedCreditNotes(
        (selected) => [
          ...selected.filter((id) => id !== creditNoteId),
          ...(newValue === true ? [creditNoteId] : []),
        ],
      );
    },
    [],
  );

  const toggleAllCreditNoteCheckboxes = () => {
    if (allChecked) {
      setSelectedCreditNotes([]);
    } else {
      setSelectedCreditNotes(filteredCreditNotes.map(note => note.id));
    }
  };

  const getSelectedCreditNotesByExcludedStates = (excludedStates: EHydraCreditNoteState[]) => {
    const selectedCreditNoteIdSet = new Set(selectedCreditNotes);

    return creditNotes
      .filter((creditNote) => selectedCreditNoteIdSet.has(creditNote.id))
      .filter((creditNote) => !excludedStates.includes(creditNote.state));
  };

  // Convert IDs to credit note objects when needed
  const getSelectedCreditNotes = () => getSelectedCreditNotesByExcludedStates([
    EHydraCreditNoteState.deleted,
    EHydraCreditNoteState.unpublished,
    EHydraCreditNoteState.void,
  ])
    .map((creditNote) => ({
      ...creditNote,
      documentType: EDocumentType["credit-note"],
    }));

  // Get count of selected credit notes that are published (not deleted, unpublished, or void)
  const getSelectedPublishedCreditNotesCount = () => getSelectedCreditNotesByExcludedStates([
    EHydraCreditNoteState.deleted,
    EHydraCreditNoteState.unpublished,
    EHydraCreditNoteState.void,
  ]).length;

  // Get count of selected credit notes that are  not published/sent yet (not deleted, unpublished)
  const getSelectedUnPublishedCreditNotesCount = () => getSelectedCreditNotesByExcludedStates([
    EHydraCreditNoteState.deleted,
    EHydraCreditNoteState.sent,
    EHydraCreditNoteState.void,
  ]).length;

  // Get count of selected credit notes that can be deleted (not already deleted)
  const getSelectedDeletableCreditNotesCount = () => getSelectedCreditNotesByExcludedStates([
    EHydraCreditNoteState.deleted,
  ]).length;

  const generatePdfsForSelected = async () => {
    // Queuing a large selection takes several requests, so the control is disabled meanwhile to
    // stop a second click queuing the same work twice.
    setIsGeneratingPdfs(true);
    try {
      await triggerCreditNotePdfGeneration(selectedCreditNotes);
    } finally {
      setIsGeneratingPdfs(false);
    }
  };

  const publishCreditNotesSelected = () => {
    const creditNotesToPublish = getSelectedCreditNotesByExcludedStates([
      EHydraCreditNoteState.deleted,
      EHydraCreditNoteState.sent,
      EHydraCreditNoteState.void,
    ]);
    publishCreditNotes(
      creditNotesToPublish.map(cn => cn.id),
      { shouldBroadcast: true },
    );
  };

  const deleteCreditNotesSelected = () => {
    // Filter out credit notes that are already deleted or in a state that can't be deleted
    const deletableIds = selectedCreditNotes.filter(id => {
      const creditNote = creditNotes.find(cn => cn.id === id);
      return creditNote?.state !== EHydraCreditNoteState.deleted;
    });

    if (deletableIds.length === 0) {
      showMessage({ message: 'No deletable credit notes selected.', severity: 'warning' });
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${deletableIds.length} credit note(s)? ` +
      'This action cannot be undone.';
    if (window.confirm(confirmMessage)) {
      deleteCreditNotes(deletableIds);
      setSelectedCreditNotes([]); // Clear selection after deletion
    }
  };

  const deleteSingleCreditNote = (creditNoteId: number) => {
    const creditNote = creditNotes.find(cn => cn.id === creditNoteId);
    if (!creditNote || creditNote.state === EHydraCreditNoteState.deleted) {
      showMessage({ message: 'This credit note cannot be deleted.', severity: 'warning' });
      return;
    }

    const confirmMessage = 'Are you sure you want to delete this credit note? This action cannot be undone.';
    if (window.confirm(confirmMessage)) {
      deleteCreditNotes([creditNoteId]);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(intVal(event.target.value));
    setPage(0); // Reset to the first page when changing rows per page
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoteNumberSearch(event.target.value);
    setPage(0); // Reset to the first page when searching
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
    setPage(0); // Reset to the first page when filtering
  };

  /**
   * Handles the multi-select state dropdown. An empty selection means "All".
   *
   * Kept on the TextField's own `onChange` (not `SelectProps.onChange`, which is typed against
   * `SelectChangeEvent<unknown>`): MUI hands a `multiple` select its value as an array but types
   * the event as a plain input change, so the value arrives as `string | string[]` and is
   * normalised to an array here rather than at each read site.
   */
  const handleStatesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFilters({ ...filters, states: Array.isArray(value) ? value : [ value ] });
    setPage(0); // Reset to the first page when filtering
  };

  const handleDateChange = (
    key: 'startDate' | 'endDate' | 'createdStartDate' | 'createdEndDate',
    date: Nullable<Date>,
  ) => {
    setFilters({ ...filters, [key]: date });
    setPage(0); // Reset to the first page when filtering by date
  };

  /**
   * The ownership-group dropdown is scoped to a single client (it loads its options from that
   * client's Freshbooks id), so the selected group is meaningless once the client selection changes.
   * Clearing it here prevents a stale group id from silently filtering the list to nothing.
   */
  const handleClientsChanged = (clients: Nullable<IClientDropdownItem[]>) => {
    setSearchClients(clients);
    setSearchOwnershipGroupId(null);
    setPage(0);
  };

  const selectedClients = searchClients ?? [];
  const singleSelectedClient = selectedClients.length === 1 ? selectedClients[0] : null;

  const handleSortChange = (field: string) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortField(field);
    setSortDirection(isAsc ? 'desc' : 'asc');
    setPage(0); // Reset to the first page when sorting
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {showSendModal && (
        <SendDocumentsModal
          show={showSendModal}
          onHide={() => setShowSendModal(false)}
          onSend={() => setSelectedCreditNotes([])}
          documents={getSelectedCreditNotes()}
        />
      )}
      <BulkSubscriptionCreditModal
        open={showBulkSubscriptionModal}
        onClose={() => {
          setShowBulkSubscriptionModal(false);
          // Refresh the credit notes list when the modal is closed
          fetchData();
        }}
      />
      <MassCreditUploadModal
        open={showMassCreditUploadModal}
        onClose={() => {
          setShowMassCreditUploadModal(false);
          // Refresh the credit notes list so any newly created drafts appear.
          fetchData();
        }}
      />
      <ApplyToInvoiceModal
        open={showApplyToInvoiceModal}
        onClose={() => setShowApplyToInvoiceModal(false)}
        onSuccess={fetchData}
        selectedCreditNotes={getSelectedCreditNotes()}
      />
      <TableContainer component={Paper}>
        {/*
          `px={10}` is 80px of padding down each side. With 13 columns that was enough to push
          the money columns off the right edge, so the table's own padding is narrowed and the
          table is given somewhere to scroll rather than clip.
        */}
        <Box px={{ xs: 2, lg: 4 }} pb={5} sx={{ overflowX: 'auto' }}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <ClientMultiSelectDropdown
                onClientChanged={handleClientsChanged}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search by Note Number"
                variant="outlined"
                size="small"
                value={noteNumberSearch}
                onChange={handleSearchChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                label="Filter by State"
                variant="outlined"
                size="small"
                name="states"
                value={filters.states}
                onChange={handleStatesChange}
                SelectProps={{
                  multiple: true,
                  // An empty selection means "All" and renders as an empty field (the label sits
                  // inline as the placeholder). The chosen states are listed in the order they
                  // appear in the menu rather than the order they happened to be clicked.
                  renderValue: (selected) => {
                    const chosen = Array.isArray(selected) ? selected : [];

                    return stateOptions
                      .filter((state) => chosen.includes(state))
                      .map(getStateFilterLabel)
                      .join(', ');
                  },
                }}
              >
                {stateOptions.map((state) => (
                  <MenuItem key={state} value={state}>
                    <Checkbox checked={filters.states.includes(state)} size="small" sx={{ py: 0 }} />
                    <ListItemText primary={getStateFilterLabel(state)} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={6}>
              <DatePicker
                label="Note Date From"
                value={filters.startDate}
                onChange={(date) => handleDateChange('startDate', date)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <DatePicker
                label="Note Date To"
                value={filters.endDate}
                onChange={(date) => handleDateChange('endDate', date)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Created From"
                value={filters.createdStartDate}
                onChange={(date) => handleDateChange('createdStartDate', date)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Created To"
                value={filters.createdEndDate}
                onChange={(date) => handleDateChange('createdEndDate', date)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Min Subtotal ($)"
                helperText="Pre-tax subtotal, not total credit"
                variant="outlined"
                size="small"
                name="minSubtotal"
                value={filters.minSubtotal}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max Subtotal ($)"
                helperText="Pre-tax subtotal, not total credit"
                variant="outlined"
                size="small"
                name="maxSubtotal"
                value={filters.maxSubtotal}
                onChange={handleFilterChange}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <OwnershipGroupDropdown
                freshbooksClientId={singleSelectedClient?.freshbooksClientId}
                ownershipGroupId={searchOwnershipGroupId}
                onOwnershipGroupChanged={(ownershipGroup) => {
                  setSearchOwnershipGroupId(ownershipGroup ? intVal(ownershipGroup.id) : null);
                  setPage(0);
                }}
                onOwnershipGroupFreeForm={() => {
                  // Free-form entry is not supported for filtering: only a real group id can be
                  // pushed into the `cn.ownershipGroupId` where-clause.
                }}
                disableIfNoOwnershipGroups={true}
                disableComponent={singleSelectedClient === null}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8} display="flex" alignItems="center">
              {singleSelectedClient === null && (
                <Typography variant="caption" color="text.secondary">
                  Select exactly one client to filter by ownership group.
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item>
                  <Link to={`/credit_notes/add_credit`}>
                    <Button variant='contained'>Add Credit Note</Button>
                  </Link>
                </Grid>
                <Grid item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => setShowBulkSubscriptionModal(true)}
                  >
                    Bulk Create Credit Notes
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => setShowMassCreditUploadModal(true)}
                  >
                    Mass Credit Upload (CSV)
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
              <Grid container spacing={2} justifyContent="flex-end">
                {/* Add PDF processing display */}
                <Grid item>

                  <Paper
                    elevation={3}
                    sx={{
                      p: 1,
                      boxShadow: '0 0 6px rgba(35, 173, 278, 1);',
                      marginRight: 2,
                    }}
                  >
                    <Typography>
                      PDFs processing:&nbsp;&nbsp;
                      <span style={{ fontSize: '1.2rem' }}>{`${creditNoteDownloadQueueLength}`}</span>
                    </Typography>
                  </Paper>

                </Grid>
                <Grid item>
                  <DropdownButton
                    as={ButtonGroup}
                    id="selected-action-dropdown"
                    variant='primary'
                    title={`Selected (${selectedCreditNotes.length} credit notes)`}
                    disabled={selectedCreditNotes.length === 0}
                  >
                    <Dropdown.Item 
                      onClick={publishCreditNotesSelected}
                      disabled={getSelectedUnPublishedCreditNotesCount() === 0}
                      title={
                        getSelectedUnPublishedCreditNotesCount() === 0
                          ? 'No unpublished credit notes selected'
                          : ''
                      }
                    >
                      Publish ({getSelectedUnPublishedCreditNotesCount()}) Credit Note(s)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowSendModal(true)}>
                      Send ({selectedCreditNotes.length}) Credit Notes to Client(s)
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setShowApplyToInvoiceModal(true)}
                      disabled={getSelectedPublishedCreditNotesCount() === 0}
                      title={getSelectedPublishedCreditNotesCount() === 0 ? "No published credit notes selected" : ""}
                    >
                      Apply ({getSelectedPublishedCreditNotesCount()}) Credit Notes to Invoice
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={generatePdfsForSelected}
                      disabled={isGeneratingPdfs}
                      title={isGeneratingPdfs ? 'PDF generation is being queued' : ''}
                    >
                      Generate ({selectedCreditNotes.length}) PDFs for Credit Notes
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={deleteCreditNotesSelected}
                      disabled={getSelectedDeletableCreditNotesCount() === 0}
                      title={getSelectedDeletableCreditNotesCount() === 0 ? "No deletable credit notes selected" : ""}
                    >
                      Delete ({getSelectedDeletableCreditNotesCount()}) Credit Note(s)
                    </Dropdown.Item>
                  </DropdownButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {!loadedState || loadedState === 'loading' ? (
            <Typography sx={{ textAlign: 'center', p: 3 }}>Loading...</Typography>
          ) : (
            <>
              <Grid container mb={2}>
                <Grid item xs={12} style={{ textAlign: 'left' }}>
                  <Button variant='outlined' onClick={toggleAllCreditNoteCheckboxes}>
                    {allChecked ? 'Deselect' : 'Select'} all {filteredCreditNotes.length} credit notes
                  </Button>
                </Grid>
              </Grid>
              <Table>
                <TableHead>
                  <StyledTableRow dark={true}>
                    <StyledTableCell dark={true} style={{ textAlign: 'center' }}>
                      <FormCheck
                        checked={allChecked && creditNotes.length > 0}
                        onChange={toggleAllCreditNoteCheckboxes}
                      />
                    </StyledTableCell>
                    <StyledTableCell dark={true} onClick={() => handleSortChange('id')} style={{ cursor: 'pointer' }}>
                      ID {renderSortIcon('id')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      onClick={() => handleSortChange('noteNumber')}
                      style={{ cursor: 'pointer' }}>
                      Note Number {renderSortIcon('noteNumber')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      onClick={() => handleSortChange('state')}
                      style={{ cursor: 'pointer' }}>
                      State {renderSortIcon('state')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      onClick={() => handleSortChange('billingAccountId')}
                      style={{ cursor: 'pointer' }}>
                      Billing Account ID {renderSortIcon('billingAccountId')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      style={{ whiteSpace: 'nowrap' }}>Client Name / Ownership Group</StyledTableCell>
                    <StyledTableCell
                      dark={true}>Source Invoice(s)</StyledTableCell>
                    <StyledTableCell
                      dark={true}>Applied To</StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      align="right"
                      onClick={() => handleSortChange('subtotalAmount')}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Subtotal {renderSortIcon('subtotalAmount')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      align="right"
                      style={{ whiteSpace: 'nowrap' }}>
                      Total Credit
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      align="right"
                      style={{ whiteSpace: 'nowrap' }}>
                      Available Credit
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}
                      onClick={() => handleSortChange('noteDate')}
                      style={{ cursor: 'pointer' }}>
                      Note Date {renderSortIcon('noteDate')}
                    </StyledTableCell>
                    <StyledTableCell
                      dark={true}>&nbsp;
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {filteredCreditNotes.length > 0 ? (
                    filteredCreditNotes.map(creditNote => (
                      <CreditNoteListDataRow
                        key={creditNote.id}
                        creditNote={creditNote}
                        checked={selectedCreditNotes.includes(creditNote.id)}
                        setSelected={setSelected}
                        onDelete={deleteSingleCreditNote}
                      />
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={13} align="center">
                        No credit notes available.
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 250, 500, 1000, 2000]}
              />
            </>
          )}
        </Box>
      </TableContainer>
    </LocalizationProvider>
  );
};
